import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { ArrowLeft } from "lucide-react";
import * as action from "../Store/_redux/LeaveStore/leaveAction";
import { useTranslation } from "react-i18next";
import { LEAVE_TYPES, LeaveTypeEnum, leaveTypeLabel } from "../modal/leave-type";

function CreateLeavePage({ onCancel, onSuccess }) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const auth = useSelector((state) => state.auth.loginUser);
  const users = useSelector((state) => state.auth.users);

  const [selectedUser, setSelectedUser] = useState(null);
  const [startDate, setStartDate] = useState("");
  const [finishDate, setFinishDate] = useState("");
  const [leaveType, setLeaveType] = useState(LeaveTypeEnum.ANNUAL);
  const [halfDay, setHalfDay] = useState(false);
  const [comment, setComment] = useState("");
  const [autoApprove, setAutoApprove] = useState(false);
  const [loading, setLoading] = useState(false);
  const [attachment, setAttachment] = useState(null);

  const calculateTotalDays = (startStr, endStr, isHalfDay) => {
    if (!startStr || !endStr) return 0;
    const start = new Date(startStr);
    const end = new Date(endStr);
    if (start > end) return 0;

    let count = 0;
    let currentDate = new Date(start);
    const lastDate = new Date(end);

    while (currentDate <= lastDate) {
      const day = currentDate.getDay();
      if (day !== 0 && day !== 6) count++;
      currentDate.setDate(currentDate.getDate() + 1);
    }

    if (isHalfDay) {
      if (count > 0) {
        count = 0.5;
      }
    }
    return count;
  };

  const totalDays = calculateTotalDays(startDate, finishDate, halfDay);

  useEffect(() => {
    if (auth?.userType !== "admin" && auth) {
      setSelectedUser(auth);
    }
  }, [auth]);

  //Rapor/Belge yükleme
  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 20 * 1024 * 1024) {
      toast.error(t("createLeave.fileSizeError"));
      return;
    }
        setAttachment(file);
    toast.success(t("createLeave.fileSelected", { name: file.name }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedUser) {
      toast.error(t("createLeave.selectUserError"));
      return;
    }
    if (!startDate || !finishDate) {
      toast.error(t("createLeave.datesRequiredError"));
      return;
    }
    if (new Date(startDate) > new Date(finishDate)) {
      toast.error(t("createLeave.endDateError"));
      return;
    }

    setLoading(true);

    const formData = new FormData();
    formData.append("tc", selectedUser.tc);
    formData.append("startDate", startDate);
    formData.append("finishDate", finishDate);
    formData.append("leavesType", leaveType);
    formData.append("isHalfDay", halfDay);
    formData.append("description", comment);
    formData.append("autoApprove", autoApprove);
    formData.append("comment", comment);
    if (attachment) {
      formData.append("attachment", attachment);
    }

    try {
      let result;

      if (auth?.userType === "admin") {
        result = await dispatch(action.createLeaveByAdmin(formData));
      } else {
        result = await dispatch(action.createLeave(formData));
      }

      if (result.status === 201) {
        toast.success(t("createLeave.successMessage"));
        await dispatch(action.getLeave(auth));
        if (onSuccess) {
          onSuccess();
        } else {
          navigate("/izinler");
        }
      } else {
        toast.error(result.data?.message || t("createLeave.errorMessage"));
      }
    } catch (error) {
      toast.error(t("createLeave.errorMessage"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={onCancel ? "w-full" : "max-w-2xl mx-auto p-6"}>
      {!onCancel && (
        <button
          onClick={() => navigate("/izinler")}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors font-medium text-sm mb-4"
        >
          <ArrowLeft size={16} />
          {t("createLeave.backToLeaves")}
        </button>
      )}

      <h1 className={`font-semibold ${onCancel ? "text-xl mb-3" : "text-2xl mb-6"}`}>{t("createLeave.title")}</h1>
      <form onSubmit={handleSubmit} className={`bg-white rounded-lg shadow ${onCancel ? "p-4 space-y-3" : "p-6 space-y-4"}`}>
        {auth?.userType === "admin" && (
          <div>
            <label htmlFor="leave-user" className="block text-sm font-medium text-gray-700 mb-1">{t("createLeave.userLabel")}</label>
            <select
              id="leave-user"
              name="user"
              value={selectedUser?._id || ""}
              onChange={(e) => {
                const user = users.find((u) => u._id === e.target.value);
                setSelectedUser(user);
              }}
              className="w-full border rounded px-3 py-2"
              required
            >
              <option value="">{t("createLeave.selectUserPlaceholder")}</option>
              {users?.map((user) => (
                <option key={user._id} value={user._id}>
                  {user.name} {user.surname} - {user.tc}
                </option>
              ))}
            </select>
          </div>
        )}

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label htmlFor="leave-start-date" className="block text-sm font-medium text-gray-700 mb-1">{t("createLeave.startDate")}</label>
            <input
              id="leave-start-date"
              name="startDate"
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="w-full border rounded px-3 py-2"
              required
            />
          </div>
          <div>
            <label htmlFor="leave-end-date" className="block text-sm font-medium text-gray-700 mb-1">{t("createLeave.endDate")}</label>
            <input
              id="leave-end-date"
              name="finishDate"
              type="date"
              value={finishDate}
              onChange={(e) => setFinishDate(e.target.value)}
              className="w-full border rounded px-3 py-2"
              required
            />
          </div>
        </div>

        {startDate && finishDate && (
          <div className="bg-blue-50 border border-blue-200 text-blue-800 rounded-lg p-3 text-sm font-semibold flex justify-between items-center">
            <span>{t("createLeave.requestedDuration")}</span>
            <span className="text-base font-bold">
              {t("createLeave.workDays", { count: totalDays })}{" "}
              <span className="text-xs font-normal">{t("createLeave.weekendsExcluded")}</span>
            </span>
          </div>
        )}

        <div>
          <label htmlFor="leave-type" className="block text-sm font-medium text-gray-700 mb-1">{t("createLeave.type")}</label>
          <select
            id="leave-type"
            name="leaveType"
            value={leaveType}
            onChange={(e) => setLeaveType(e.target.value)}
            className="w-full border rounded px-3 py-2"
          >
            {LEAVE_TYPES.map((type) => (
              <option key={type} value={type}>{leaveTypeLabel(t, type)}</option>
            ))}
          </select>
        </div>

        <div className="flex items-center">
          <input
            type="checkbox"
            id="halfDay"
            checked={halfDay}
            onChange={(e) => setHalfDay(e.target.checked)}
            className="mr-2"
          />
          <label htmlFor="halfDay" className="text-sm text-gray-700">{t("createLeave.halfDay")}</label>
        </div>

        <div>
          <label htmlFor="leave-comment" className="block text-sm font-medium text-gray-700 mb-1">{t("createLeave.notes")}</label>
          <textarea
            id="leave-comment"
            name="comment"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            rows="3"
            className="w-full border rounded px-3 py-2"
          />
        </div>
        <div>
          <input
            type="file"
            id="fileInput"
            accept=".pdf,.docx"
            onChange={handleFileChange}
            style={{ display: "none" }}
          />
          <button
            type="button"
            onClick={() => document.getElementById("fileInput").click()}
            className="w-full bg-blue-600 text-white py-2 rounded hover:bg-blue-700"
          >
            {attachment ? `📄 ${attachment.name}` : t("createLeave.uploadReport")}
          </button>
        </div>
        

        {auth?.userType === "admin" && (
          <div className="flex items-center">
            <input
              type="checkbox"
              id="autoApprove"
              checked={autoApprove}
              onChange={(e) => setAutoApprove(e.target.checked)}
              className="mr-2"
            />
            <label htmlFor="autoApprove" className="text-sm text-gray-700">
              {t("createLeave.autoApprove")}
            </label>
          </div>
        )}

        <button
          type="submit"
          disabled={loading}
          className="w-full bg-blue-600 text-white py-2 rounded hover:bg-blue-700 disabled:bg-blue-300"
        >
          {loading ? t("createLeave.submitting") : t("createLeave.submit")}
        </button>
      </form>
    </div>
  );
}

export default CreateLeavePage;
